// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xIsAuthenticated } from "@rpsc/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@rpsc/App/Store/Selectors/SettingsSelectors";
import StyledBox from "@rpsc/XDL/Components/Common/Box";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import IconPhone from "@rpsc/XDL/Icons/IconPhone";
import parse from "html-react-parser";
import * as React from "react";

const enum fields {
    title = "title",
    content = "content",
    phone = "phone",
    // Field to show phone for authenticated and non-authenticated users
    showForAuthenticated = "showForAuthenticated",
    showPhoneIcon = "showPhoneIcon",
}

interface OwnProps extends WidgetProps {
    fields: {
        title: string;
        content: string;
        phone: string;
        showForAuthenticated: boolean;
        showPhoneIcon: boolean;
    };
}

export interface QuestionsWidgetStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    content?: TypographyProps;
    phone?: LinkPresentationProps;
    salesRepWrapper?: InjectableCss;
    salesRepName?: TypographyProps;
    salesRepTitle?: TypographyProps;
    separatorWrapper?: InjectableCss;
}

export const questionsWidgetStyles: QuestionsWidgetStyles = {};
const styles = questionsWidgetStyles;

const QuestionsWidget: React.FC<OwnProps> = ({
    fields: { title, content, phone, showForAuthenticated, showPhoneIcon },
}) => {
    const { salesPersonInformation, isAuthenticated } = useAppSelector(state => {
        const salesPersonInformation = getSettingsCollection(state).insideSalesSettings;

        return {
            isAuthenticated: xIsAuthenticated(state),
            salesPersonInformation,
        };
    });
    return (
        <StyledBox {...styles.container} className="questions-widget-wrapper">
            {title && <Typography {...styles.title}>{title}</Typography>}
            {content && <Typography {...styles.content}>{parse(content, parserOptions)}</Typography>}
            {phone && (showForAuthenticated || !isAuthenticated) && (
                <Link
                    href={`tel:${phone}`}
                    {...styles.phone}
                    target="_blank"
                    aria-label={phone}
                    icon={{
                        iconProps: {
                            src: showPhoneIcon ? IconPhone : "",
                            size: 16,
                        },
                    }}
                >
                    {phone}
                </Link>
            )}
            {phone && showForAuthenticated && isAuthenticated && <StyledWrapper {...styles.separatorWrapper} />}
            {isAuthenticated && (salesPersonInformation.phoneNumber || salesPersonInformation.emailAddress) && (
                <StyledWrapper {...styles.salesRepWrapper} {...{ shoBorderTop: phone && showForAuthenticated }}>
                    <Typography {...styles.salesRepTitle}>{translate("Contact Inside Sales")}</Typography>

                    {salesPersonInformation.phoneNumber && (
                        <Link
                            href={`tel:${salesPersonInformation.phoneNumber}`}
                            {...styles.phone}
                            target="_blank"
                            aria-label={salesPersonInformation.phoneNumber}
                        >
                            {salesPersonInformation.phoneNumber}
                        </Link>
                    )}
                    {salesPersonInformation.emailAddress && (
                        <Link
                            href={`mailto:${salesPersonInformation.emailAddress}`}
                            {...styles.phone}
                            target="_blank"
                            aria-label={salesPersonInformation.emailAddress}
                        >
                            {salesPersonInformation.emailAddress}
                        </Link>
                    )}
                </StyledWrapper>
            )}
        </StyledBox>
    );
};

const questionsWidget: WidgetModule = {
    component: QuestionsWidget,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.content,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.phone,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.showForAuthenticated,
                displayName: "show phone number for Authenticated Users",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showPhoneIcon,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default questionsWidget;
